import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CpuChipIcon,
  BoltIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CogIcon,
  RocketLaunchIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const BotsTrading = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const features = [
    {
      icon: CpuChipIcon,
      title: 'AI-Powered Algorithms',
      description: 'Advanced machine learning algorithms that adapt to market conditions and optimize trading strategies in real-time.'
    },
    {
      icon: BoltIcon,
      title: 'Lightning Fast Execution',
      description: 'Sub-millisecond order execution with automated risk management and position sizing for optimal performance.'
    },
    {
      icon: ChartBarIcon,
      title: 'Backtesting Engine',
      description: 'Test your strategies against historical data with comprehensive analytics and performance metrics before going live.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Risk Management',
      description: 'Built-in risk controls including stop-loss, take-profit, and position limits to protect your capital.'
    },
    {
      icon: ClockIcon,
      title: '24/7 Trading',
      description: 'Automated bots never sleep. Trade around the clock across global markets without manual intervention.'
    },
    {
      icon: CogIcon,
      title: 'Custom Strategies',
      description: 'Build your own trading bots or choose from our library of proven strategies created by expert traders.'
    }
  ];

  const botTypes = [
    {
      name: 'Grid Trading Bot',
      description: 'Automatically places buy and sell orders at predetermined intervals to profit from market volatility.',
      profit: '+15-25%',
      risk: 'Medium',
      image: '/images/bot-trade.jpg'
    },
    {
      name: 'DCA Bot',
      description: 'Dollar Cost Averaging bot that spreads purchases over time to reduce impact of volatility.',
      profit: '+10-20%',
      risk: 'Low',
      image: '/images/trading.png'
    },
    {
      name: 'Arbitrage Bot',
      description: 'Detects price differences across exchanges and executes trades automatically for risk-free profits.',
      profit: '+5-15%',
      risk: 'Low',
      image: '/images/bitcoin-cryptocurrency-trading-with-candlestick-technical-analytics_441990-14552.jpg'
    },
    {
      name: 'Momentum Bot',
      description: 'Identifies and trades on market momentum using technical indicators and trend analysis.',
      profit: '+20-40%',
      risk: 'High',
      image: '/images/stock-market-economic-graph-with-diagrams-business-financial-concepts-reports-abstract-technology-communication-concept-background_29865-1793.jpg'
    }
  ];

  const stats = [
    { label: 'Active Bots', value: '12,500+' },
    { label: 'Total Profit', value: '$47.2M+' },
    { label: 'Success Rate', value: '87.3%' },
    { label: 'Avg Return', value: '+18.5%' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 bg-gradient-to-br from-slate-950 via-slate-900 to-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full">
                  <span className="text-amber-300 text-sm font-medium">Automated Trading</span>
                </div>
                
                <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Bots Trading
                  <span className="block bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent mt-2">
                    Powered by AI
                  </span>
                </h1>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Automate your trading strategies with AI-powered bots that execute trades 24/7. 
                  Set your parameters and let advanced algorithms work for you while you sleep.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-500/50"
                >
                  Start Trading
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-amber-500/50 text-white font-semibold text-lg rounded-xl transition-all duration-300">
                  <PlayIcon className="mr-2 h-5 w-5" />
                  Watch Demo
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-gray-800">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-amber-400 mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border-2 border-amber-500/30 shadow-2xl">
                <img
                  src="/images/bot-trade.jpg"
                  alt="AI Trading Bots"
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.src = '/images/24379.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">24/7</div>
                    <div className="text-xs text-gray-300">Automated</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 lg:py-32 bg-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Everything you need to automate your trading and maximize your returns
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white rounded-2xl border-2 border-gray-200 p-8 hover:border-amber-500 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-200 transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bot Types Section */}
      <section className="relative py-24 lg:py-32 bg-black overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-500"></div>
              <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-500"></div>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Pre-Built Trading Bots
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Choose from our library of proven trading strategies or build your own
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {botTypes.map((bot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border-2 border-amber-500/30 overflow-hidden hover:border-amber-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/20"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={bot.image}
                    alt={bot.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-white">{bot.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      bot.risk === 'Low' ? 'bg-green-500/20 text-green-400' :
                      bot.risk === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {bot.risk} Risk
                    </span>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">{bot.description}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-2xl font-bold text-green-400">{bot.profit}</div>
                      <div className="text-sm text-gray-400">Avg Profit</div>
                    </div>
                    <button className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors duration-300">
                      Use Bot
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-amber-50 to-yellow-50 overflow-hidden">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Ready to Automate Your Trading?
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Start using AI-powered trading bots today and let automation work for you 24/7
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Get Started Free
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/trading"
                className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-amber-500 text-gray-900 font-semibold text-lg rounded-xl transition-all duration-300"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BotsTrading;

